const en = {
  common: {
    translateTo: "Translate to {{lang}}",
    translating: "Translating…",
    showOriginal: "Show original",
    translatedByAi: "Translated by AI",
  },
  nav: {
    connect: "Connect",
    platform: "Platform",
    pricing: "Pricing",
    docs: "Docs",
    blog: "Blog",
    about: "About",
    changelog: "Changelog",
    signIn: "Sign in",
    requestAccess: "Get started",
    language: "Language",
  },
  footer: {
    tagline:
      "We're building the AI Twin platform for modern companies. Every business deserves an always-on digital twin that talks to customers, qualifies leads and surfaces opportunities.",
    ownedBy: "A project by {{company}}, owned by {{owner}}.",
    rights: "© {{year}} Ruznic Marketing · Reality Twin",
    buildingInPublic: "available now — built in Switzerland",
    col: {
      product: "Product",
      resources: "Resources",
      company: "Company",
      legal: "Legal",
    },
    dir: {
      topics: "Topics",
      industries: "Industries",
    },
    links: {
      topics: "Topics",
      platform: "Platform",
      twinCheck: "Twin-Check",
      roadmap: "Roadmap",
      pricing: "Pricing",
      network: "Network",
      changelog: "Changelog",
      docs: "Docs",
      blog: "Blog",
      about: "About",
      customers: "Customers",
      careers: "Careers",
      invest: "Invest & Partner",
      contact: "Contact",
      impressum: "Imprint",
      privacy: "Privacy",
      terms: "Terms",
      dpa: "DPA",
      security: "Security",
      subprocessors: "Sub-processors",
      nuvatr: "Nuvatr",
      connect: "Connect",
      vsChatbase: "vs. Chatbase",
      useCases: "Use cases",
      industries: "Industries",
      vsBotpress: "vs. Botpress",
      compare: "Compare alternatives",
      faq: "FAQ",
      support: "Support",
    },
  },
  nuvatr: {
    hero: {
      eyebrow: "Partnership · Sister venture",
      titlePre: "Reality Twin",
      titleAfter: "Nuvatr.",
      sub: "Nuvatr is our sister venture — an international business registry building the Global Trade Number (GTN): one unified, verified identifier for every company on earth. Reality Twin subscribers get a permanent discount and one-click data sync.",
    },
    cobrand: {
      taglinePre: "Two products of ",
      taglinePost: ", built in Hinwil, Switzerland — designed to work together.",
    },
    what: {
      eyebrow: "What is Nuvatr",
      titleA: "One global identifier",
      titleEm: "for every business.",
      p1: "Over 200 million SMEs worldwide are practically invisible online — no SEO, no backlinks, no verified profile. Large directories are fragmented per country, per language, per industry.",
      p2Pre:
        "Nuvatr is building the international business registry: a single, verified profile per company, globally addressable through the",
      gtnLabel: "Global Trade Number (GTN)",
      p2Post:
        "— think DUNS, but open, public and accessible to every business regardless of size or budget.",
      p3: "One entry. 195+ countries. Real customer reviews, employee feedback, all locations, services and contact data — discoverable by humans, search engines and AI assistants alike.",
    },
    deliver: {
      eyebrow: "What Nuvatr delivers",
      titleA: "A verified, global",
      titleEm: "company profile.",
      features: [
        {
          title: "Global Trade Number",
          desc: "A globally unified company identifier — like DUNS, but for everyone.",
        },
        {
          title: "195+ countries",
          desc: "Your business becomes discoverable worldwide, in every language, for every market.",
        },
        {
          title: "DoFollow backlinks",
          desc: "High-quality SEO backlinks that improve your Google ranking from day one.",
        },
        {
          title: "Real reviews",
          desc: "Authentic customer and employee reviews — verified, not bought.",
        },
        {
          title: "All locations",
          desc: "Every office, store, branch and service area in one structured profile.",
        },
        {
          title: "Know the company",
          desc: "Customers and partners discover who you are, what you do, and how you work.",
        },
      ],
    },
    benefits: {
      eyebrow: "Reality Twin subscriber benefits",
      titleA: "Active subscribers get Nuvatr at",
      titleEm: "a fraction of the price.",
      sub: "Reality Twin already knows your company inside out — products, services, locations, FAQs. We push that knowledge directly into your Nuvatr profile, so you never re-type a thing.",
      listing: {
        eyebrow: "Connected listing",
        desc: "One-click Nuvatr listing for every Reality Twin subscriber, powered by your Twin's knowledge graph. Auto-synced, verified, SEO-optimized — for less than the price of a coffee.",
        bullets: [
          "Auto-import from your Reality Twin knowledge graph",
          "Verified Nuvatr profile with DoFollow backlink",
          "Global Trade Number assigned automatically",
          "Live sync — update once, propagate everywhere",
        ],
      },
      discount: {
        eyebrow: "Across the board",
        on: "on everything",
        desc: "As long as your Reality Twin subscription is active, you get up to 30% off every Nuvatr service, feature and add-on — claim, verification, premium placement, analytics, API access.",
        bullets: [
          "Up to 30% off every Nuvatr plan & add-on",
          "Discount renews with your Reality Twin subscription",
          "Stacks with annual billing on Nuvatr",
          "Applies to new and existing Nuvatr listings",
        ],
      },
      footnote:
        "Pricing comparison based on Nuvatr's public list price. Discounts require an active Reality Twin subscription; benefits pause if the subscription lapses.",
    },
    how: {
      eyebrow: "How it works",
      titleA: "From Reality Twin to Nuvatr",
      titleEm: "in one click.",
      steps: [
        {
          n: "01",
          title: "Your Twin learns your company",
          desc: "Reality Twin indexes your website, docs and team into a citable knowledge graph — products, locations, FAQs, services.",
        },
        {
          n: "02",
          title: "Connect Nuvatr in one click",
          desc: "Inside your Reality Twin workspace, link Nuvatr. We map the right fields, create your GTN and submit your verified profile.",
        },
        {
          n: "03",
          title: "Stay in sync, automatically",
          desc: "Whenever your Twin learns something new — a new location, a price change, a review — your Nuvatr listing updates the same day.",
        },
      ],
    },
    cta: {
      eyebrow: "Ready when you are",
      titleA: "Two products.",
      titleEm: "One company truth.",
      sub: "Get your AI Twin live first — Nuvatr unlocks automatically inside your workspace, with the partnership pricing already applied.",
      primary: "Get started",
      secondary: "Visit Nuvatr",
      footnote:
        "Nuvatr currently operates as a sister venture of Ruznic Marketing. Partnership terms reviewed annually.",
    },
  },
  home: {
    checkPlaceholder: "yourcompany.com",
    checkAria: "Your website address",
    checkCta: "See how AI reads you",
    checkHint: "Free, no account — then ask that company's Twin anything.",
    mcp: {
      docs: "Read the docs",
      eyebrow: "Live today",
      title: "Your company, reachable inside ChatGPT and Claude.",
      body: "Decision-makers research in AI assistants now, not search engines. Connect this address once and your twin answers them there — no website visit, no click, no search result.",
      copy: "Copy",
      copied: "Copied",
      how: "How to connect",
      agentCard: "Agent Card",
    },
    ctaCheck: "Check your AI-readability — free",
    badge: "Available now for modern companies",
    heroTitle: "Every company deserves",
    heroTitleEm: "an AI Twin.",
    heroSub:
      "Reality Twin turns your website into a conversational digital twin of your business — one that answers customers, qualifies leads and surfaces opportunities. Create an account and launch your Twin today.",
    ctaPrimary: "Get started",
    ctaSecondary: "Talk to the team",
    logoStripEyebrow: "Available now",
    logoStripBody:
      "Reality Twin is built by Ruznic Marketing in Hinwil, Switzerland. SMBs, agencies and growing teams use it to answer customers, qualify leads and turn company knowledge into conversations. Customer stories will appear here as teams go live.",
    how: {
      eyebrow: "How it works",
      titleA: "From website to AI Twin",
      titleEm: "in 60 seconds.",
      steps: [
        {
          title: "Connect your website",
          desc: "Drop any URL. We crawl every public page in seconds — products, mission, pricing, docs.",
        },
        {
          title: "Build the knowledge graph",
          desc: "RTN indexes everything into a private, citable knowledge graph. Answers are generated from your own content and shown with source citations.",
        },
        {
          title: "Tune your twin",
          desc: "Define voice, tone, do's and don'ts. Test personalities, then ship.",
        },
        {
          title: "Customers converse",
          desc: "Visitors, partners and investors chat with your twin in real time, 24/7.",
        },
        {
          title: "Opportunities surface",
          desc: "The Twin-to-Twin network finds aligned partners and qualified leads automatically.",
        },
      ],
    },
    features: {
      eyebrow: "Platform",
      titleA: "The intelligence layer",
      titleEm: "for your company.",
      sub: "One twin. Infinite surfaces. From your homepage to inbound sales, partnerships and capital raises.",
      items: [
        {
          title: "Knowledge engine",
          desc: "Indexes your site, docs and decks into a private graph. Every answer is generated from your own content and shown with source citations.",
        },
        {
          title: "Twin-to-Twin protocol",
          desc: "Your twin talks to other twins to surface partners, customers and capital — without outreach.",
        },
        {
          title: "Opportunity engine",
          desc: "Qualified leads and partnerships scored with confidence, routed where they belong.",
        },
        {
          title: "Lead capture & scoring",
          desc: "Every conversation becomes a scored, enriched lead synced to your CRM.",
        },
        {
          title: "Marketplace presence",
          desc: "Get discovered by investors, customers and collaborators across the network.",
        },
        {
          title: "Privacy & security first",
          desc: "Encrypted at rest and in transit, per-workspace isolation, Swiss/EU hosting. Honest about where we are.",
        },
      ],
    },
    live: {
      k1: "Every public page of your website",
      k2: "Documents you upload (PDF, docs, price lists)",
      k3: "Answers cited back to their source",
      k4: "Re-crawled on a schedule, so it stays current",
      k5: "Questions it couldn't answer, reported to you",
      autoTitleHonest: "Your twin looks for matches while you sleep",
      autoBodyHonest:
        "It scans the network daily and surfaces companies worth talking to — you decide who gets an introduction.",
      q1: "What makes Reality Twin different?",
      q2: "What does it cost?",
      q3: "How long does setup take?",
      prompt: "This is our own twin, answering live from this website. Ask it anything.",
      placeholder: "Ask the twin anything…",
      failed: "The twin couldn't answer that right now — try another question.",
      eyebrow: "Live",
      titleA: "See your twin",
      titleEm: "in action.",
      sub: "This is what a visitor experiences on a twin-powered site — every answer grounded in your knowledge graph.",
      indexEyebrow: "Knowledge index",
      indexTitle: "What the twin knows",
      autoEyebrow: "Auto-discovered",
      autoTitle: "3 matched partners in EU fintech",
      autoConfidence: "Confidence 92% — ready to introduce.",
    },
    cases: {
      eyebrow: "Use cases",
      titleA: "Built for every team that",
      titleEm: "talks to the world.",
      items: [
        {
          title: "Inbound sales",
          desc: "Every visitor gets a tailored conversation. Hot leads land in your CRM scored and routed.",
        },
        {
          title: "Partnerships",
          desc: "Your twin meets compatible twins and proposes intros — without anyone sending an email.",
        },
        {
          title: "Investor relations",
          desc: "Investors ask, your twin answers with current metrics, decks and traction.",
        },
        {
          title: "Talent attraction",
          desc: "Candidates explore your team, mission and openings in a conversation.",
        },
      ],
    },
    partners: {
      eyebrow: "Customers",
      titleA: "Built for teams that",
      titleEm: "move fast.",
      body: "Reality Twin is available for companies that want customer conversations, qualification and knowledge answers running without waiting for an approval queue.",
      cta: "Talk to the team",
    },
    pricing: {
      eyebrow: "Pricing",
      titleA: "Simple,",
      titleEm: "scales with you.",
      sub: "Start free. Upgrade when your twin starts paying for itself.",
      compare: "Compare every plan",
      popular: "Popular",
      perMo: "/mo",
    },
    faq: {
      eyebrow: "FAQ",
      titleA: "Questions,",
      titleEm: "answered.",
      items: [
        {
          q: "How long does it take to create a twin?",
          a: "Around 60 seconds for a typical site. Larger knowledge bases take a few minutes to finish indexing.",
        },
        {
          q: "Can I control my twin's personality?",
          a: "Yes — tune voice, tone, verbosity, what it should refuse and when it hands over to a human. Changes take effect immediately.",
        },
        {
          q: "Does the twin stay up to date?",
          a: "Your website is re-crawled on a schedule and you can upload documents any time. Questions it couldn't answer are reported to you, so you can close the gaps.",
        },
        {
          q: "Is my data private?",
          a: "Your knowledge base is isolated per company, hosted in the EU (Ireland), and covered by a DPA. We don't train models on your data.",
        },
        {
          q: "What does Twin-to-Twin do?",
          a: "Your twin scans the network for companies worth talking to and drafts the outreach. You decide who actually gets an introduction.",
        },
      ],
    },
    whatIs: {
      eyebrow: "What is Reality Twin Network",
      titleA: "Every company has a website.",
      titleEm: "We give it a voice.",
      p1: "Websites are static. Reality Twin Network (RTN) turns yours into an AI Twin — an intelligent digital version of your company that talks to customers, partners and investors in real time.",
      p2: "Instead of searching, people converse. Instead of hunting for partners, twins find each other. Instead of analyzing hundreds of startups, investors receive the most relevant matches automatically.",
      p3: "It's the next layer of the internet — after websites, social networks and search engines.",
      cta: "Read the full vision",
    },
    cta: {
      eyebrow: "Get started",
      titleA: "Start your",
      titleEm: "AI Twin.",
      sub: "Create an account, connect your website and get your Twin running with founder-led support from Hinwil, Switzerland.",
      primary: "Get started",
      secondary: "Read the docs",
    },
  },
  pages: {
    pricing: {
      eyebrow: "Pricing",
      title: "Pricing that scales with your twin.",
      sub: "Start free. Pay only when your AI Twin starts moving the needle for your business.",
    },
    about: {
      eyebrow: "About",
      title: "Building Switzerland's AI Twin platform.",
      sub: "Reality Twin is built by Ruznic Marketing in Hinwil — a small studio that believes every company deserves a digital twin.",
    },
    network: {
      eyebrow: "Network",
      title: "A network of AI Twins, talking to each other.",
      sub: "The public side of Reality Twin — discover the companies, products and people building inside the network.",
    },
    customers: {
      eyebrow: "Customers",
      title: "Built for companies that actually talk to people.",
      sub: "Reality Twin gives every business — from solo founders to 200-person teams — an AI Twin that handles inbound conversations, qualifies leads and frees your team for real work.",
    },
    careers: {
      eyebrow: "Careers",
      title: "Help us build the AI Twin layer of the internet.",
      sub: "A small, opinionated team in Hinwil, Switzerland. Remote-friendly across the EU. Hiring slowly, deliberately.",
    },
    contact: {
      eyebrow: "Contact",
      title: "Talk to the team behind Reality Twin.",
      sub: "Press, partnerships, sales, support — or just curious about the. Real humans, real answers.",
    },
    docs: {
      eyebrow: "Documentation",
      title: "Reality Twin documentation.",
      sub: "Everything you need to build, deploy and operate your AI Twin — guides, references and recipes.",
    },
    changelog: {
      eyebrow: "Changelog",
      title: "What's new in Reality Twin.",
      sub: "Every meaningful release, fix and product change — shipped from Hinwil, in public.",
    },
    security: {
      eyebrow: "Security",
      title: "Security & trust at Reality Twin.",
      sub: "How we protect your data, your customers' conversations and your knowledge graph — under Swiss FADP and EU GDPR.",
    },
    privacy: {
      eyebrow: "Legal",
      title: "Privacy notice.",
      sub: "How Ruznic Marketing collects, processes and protects personal data on Reality Twin.",
    },
    terms: {
      eyebrow: "Legal",
      title: "Terms of service.",
      sub: "The rules of the road for using Reality Twin.",
    },
    dpa: {
      eyebrow: "Legal",
      title: "Data Processing Agreement.",
      sub: "GDPR Article 28 / Swiss FADP-compliant DPA between Ruznic Marketing and customers.",
    },
    subprocessors: {
      eyebrow: "Legal",
      title: "Sub-processors.",
      sub: "Third-party services we use to operate Reality Twin, with purpose and region.",
    },
    invest: {
      eyebrow: "Invest & Partners",
      title: "Help us make AI Twins accessible to every company.",
      sub: "Reality Twin Network is actively raising funds and looking for partners who believe every business — not just the biggest — deserves an intelligent digital twin.",
    },
    platform: {
      eyebrow: "Platform",
      title: "What is Reality Twin Network?",
      sub: "Every company has a website. RTN turns it into an intelligent digital twin that talks to customers, partners and investors — and meets other twins in the network.",
    },
  },
  platform: {
    intro: {
      p1: "Every company has a website today. Many have social channels, CRMs and digital business cards. They all share one problem — they're static.",
      p2: "Companies evolve every single day, but their digital presence stays outdated, impersonal and passive. Customers have to search. Partners spend hours researching. Investors burn afternoons analyzing companies one by one.",
      p3: "Reality Twin Network changes this from the ground up.",
    },
    twin: {
      eyebrow: "The AI Twin",
      titleA: "An intelligent digital twin",
      titleEm: "for every company.",
      body: "RTN creates an AI Twin for every business. The twin understands your company — its products, services, vision, goals and information — and communicates with customers, partners and investors in real time.",
      knowsTitle: "What your twin learns from",
      knows: [
        "Company information",
        "Products and services",
        "FAQs",
        "Blog posts",
        "Whitepapers",
        "Presentations",
        "Documentation",
      ],
      shiftsTitle: "What changes",
      shifts: [
        "Instead of searching a website, people talk directly with the company.",
        "Instead of hunting for partners for hours, AI Twins find each other.",
        "Instead of analyzing hundreds of startups, investors automatically receive the most relevant projects for their interests.",
      ],
    },
    how: {
      eyebrow: "How it works",
      titleA: "From URL to AI Twin",
      titleEm: "in minutes.",
      steps: [
        {
          n: "01",
          title: "Provide your website",
          desc: "A company simply enters its URL.",
        },
        {
          n: "02",
          title: "RTN analyzes everything",
          desc: "Company info, products, services, FAQs, blog posts, whitepapers, presentations and documentation.",
        },
        {
          n: "03",
          title: "Your twin is born",
          desc: "Within minutes RTN builds your digital twin — ready to talk.",
        },
      ],
      capsTitle: "What your AI Twin can do",
      caps: [
        "Answer customer questions",
        "Explain products and services",
        "Generate leads",
        "Inform investors",
        "Find partners",
        "Identify business opportunities",
        "Communicate around the clock",
      ],
    },
    network: {
      eyebrow: "The next evolution",
      titleA: "Twins that",
      titleEm: "talk to each other.",
      body: "Long term, RTN becomes the world's first network of intelligent company twins. Companies no longer only communicate with humans — they communicate with each other.",
      finds: ["Customers", "Suppliers", "Investors", "Influencers", "Partners", "New markets"],
      close:
        "A completely new digital ecosystem where business opportunities are no longer searched for — they surface automatically.",
    },
    why: {
      eyebrow: "Why RTN",
      titleA: "The next layer",
      titleEm: "of the internet.",
      ladder: [
        "The world built websites.",
        "Then social networks.",
        "Then search engines.",
        "Now begins the age of intelligent digital twins.",
      ],
      body: "Reality Twin Network is building the infrastructure for this new generation of business communication.",
    },
    closing: {
      titleA: "Every company deserves",
      titleEm: "a digital twin.",
      lines: [
        "Every idea deserves a voice.",
        "Every opportunity deserves to be found.",
        "Welcome to Reality Twin Network.",
        "The future doesn't start tomorrow.",
        "It starts with the first digital twin.",
      ],
      ctaPrimary: "Get started",
      ctaSecondary: "Talk to the team",
    },
  },
  invest: {
    intro: {
      p1: "We're building the next layer of the internet: a global network of intelligent digital twins for every company. Websites became static. Social media became noise. Search engines became advertising. We're building what comes next.",
      p2: "To bring this vision to every business — from a one-person studio in Hinwil to a thousand-person enterprise in São Paulo — we need capital, partners and people who want to build the infrastructure with us.",
      p3: "This page exists because we're being honest: Reality Twin Network is publicly available, we're early, and we're actively looking for the right investors and partners to scale what's already working.",
    },
    raise: {
      eyebrow: "We're raising",
      titleA: "Capital to build",
      titleEm: "the twin network.",
      body: "We're opening a focused funding round to scale the platform, the AI infrastructure and the team behind Reality Twin Network. If you back early-stage AI, B2B SaaS, vertical infrastructure or European deep tech — let's talk.",
      useTitle: "Where the capital goes",
      use: [
        "Scaling the AI Twin engine — ingestion, knowledge graph, conversation quality.",
        "Building the Twin-to-Twin discovery protocol — the network effect.",
        "Hiring senior engineers, AI researchers and a first product designer.",
        "Compliance, security and EU/Swiss data residency.",
        "Onboarding customers across DACH and the EU.",
      ],
    },
    partners: {
      eyebrow: "Strategic partners",
      titleA: "Partners who help us",
      titleEm: "open the door.",
      body: "Capital alone doesn't make a network. We're looking for partners who can help us reach more companies, integrate Reality Twin into existing workflows, and build trust at scale.",
      seekTitle: "Who we're looking for",
      seek: [
        "Agencies and consultancies who want to deploy AI Twins for their clients.",
        "Integrators and resellers in the DACH, EU and global SMB market.",
        "CRM, CMS and marketing platforms that want to embed the Twin layer.",
        "Industry associations and chambers of commerce championing SMB digitalization.",
        "Angels, operators and advisors with deep AI, SaaS or go-to-market experience.",
      ],
    },
    vision: {
      eyebrow: "Why now",
      titleA: "Every business deserves",
      titleEm: "to be found.",
      body: "Today, only the companies with the biggest marketing budgets get discovered. We believe a one-person studio in a small town deserves the same chance to reach customers, investors and partners as a global brand. The twin network makes that possible — and we need partners to make it real.",
    },
    cta: {
      titleA: "Let's build the next layer",
      titleEm: "of the internet together.",
      body: "Investor decks, partnership briefings and personal conversations — we reply within one business day.",
      primary: "Contact founders",
      secondary: "Email hello@realitytwin.io",
    },
  },
  app: {
    pages: {
      agentTraffic: {
        title: "Agent traffic",
        subtitle:
          "Which AI agents looked your company up, how, and what they wanted — last 30 days. This data only exists because your twin is the endpoint they query.",
        requests: "requests",
        empty:
          "No agent traffic yet. Publish your twin and share your Agent Card — agents find you through the network, MCP and your public profile.",
        byAgent: "By agent",
        bySurface: "How they reached you",
        asked: "What agents asked",
        sCard: "Agent Card lookup",
        sMcp: "Asked via MCP",
        sChat: "Chat",
      },
      khealth: {
        title: "Knowledge health",
        subtitle:
          "How current your sources are, and whether they contradict each other. Outdated facts get stated confidently unless you catch them.",
        score: "Freshness",
        fresh: "fresh",
        aging: "aging",
        stale: "stale",
        today: "today",
        daysAgo: "{{count}}d ago",
        showAll: "Show all {{count}}",
        showLess: "Show less",
        contraTitle: "Contradiction check",
        contraHelp:
          "Finds places where your sources state incompatible facts — two different prices for the same plan, conflicting delivery times.",
        runCheck: "Run check",
        checking: "Checking…",
        none: "No contradictions found.",
      },
      register: {
        lvl_informational: "informational",
        lvl_indicative: "indicative",
        lvl_binding: "binding",
        bindingTitle: "Allow binding statements",
        bindingHelp:
          "Off by default. While off, your twin never promises prices, dates or terms — it gives indicative guidance and defers to your team for written confirmation.",
        title: "Statement register",
        subtitle:
          "Every answer your twin gives the outside world — timestamped, source-attributed and hash-anchored. Auditable proof of what was said, and on what basis.",
        export: "Export JSON",
        exporting: "Exporting…",
        empty: "No statements yet. Once your twin answers visitors, every answer is logged here.",
        total: "Logged",
        sources: "sources",
        noSources: "no sources",
      },
      verify: {
        m_dns: "DNS record",
        m_meta: "Meta tag",
        m_file: "File upload",
        howDns:
          "Add a TXT record to your domain, then click Verify. DNS changes can take a few minutes to propagate.",
        howMeta: "Add this tag inside the <head> of your homepage, then click Verify.",
        howFile: "Put this token in a file at /.well-known/reality-twin.txt, then click Verify.",
        dnsType: "Type",
        dnsHost: "Host",
        dnsHostAlt: "(or @ for the root domain)",
        legalForm: "Legal form",
        lfChoose: "Please choose…",
        lfSole: "Sole proprietorship (Einzelfirma)",
        lfCompany: "Registered company (GmbH, AG, Ltd…)",
        lfPartnership: "Partnership",
        lfAssociation: "Association / foundation",
        lfOther: "Other",
        registerOptional: "Commercial register no. (optional)",
        soleHint:
          "A sole proprietorship only has to register above CHF 100'000 turnover — leave this empty if you're not registered.",
        vatOptional: "VAT number (optional)",
        vatHint: "Only if you're VAT-registered. Not every business is.",
        title: "Company verification",
        stVerified: "Verified",
        stPending: "Pending",
        stNone: "Not verified",
        subtitle:
          "Prove you own your domain to earn a Verified seal on your Twin — the trust other companies' agents check first. No seal without real proof.",
        legalName: "Legal company name",
        register: "Commercial register no. (e.g. CHE-…)",
        country: "Country",
        save: "Save details",
        saving: "Saving…",
        saved: "Saved",
        proveTitle: "Prove domain ownership",
        proveHow:
          "Add this tag to your homepage <head> — or put the token in a file at /.well-known/reality-twin.txt — then click Verify.",
        needWebsite: "Connect your website in the Company tab first, then verify here.",
        copied: "Copied",
        verifying: "Verifying…",
        verifyCta: "Verify domain ownership",
        verifiedLine: "Domain ownership verified. Your Twin now shows a Verified seal.",
        ok: "Domain verified — your Twin is now Verified.",
        notFound: "We couldn't find the token on your site yet. Add it and try again.",
      },
      common: {
        exportCsv: "Export CSV",
        search: "Search…",
        all: "All",
        save: "Save",
        saving: "Saving…",
        cancel: "Cancel",
        loading: "Loading…",
        noData: "No data yet.",
      },
      status: {
        new: "New",
        hot: "Hot",
        warm: "Warm",
        cold: "Cold",
        won: "Won",
        lost: "Lost",
      },
      leads: {
        title: "Leads",
        subtitle: "AI-qualified by intent & lead score.",
        searchPh: "Search name, email, question…",
        empty:
          "No leads yet. Leads appear here when visitors chat with your twin and share contact info.",
        openTwin: "Open your twin →",
        colName: "Name",
        colEmail: "Email",
        colQuestion: "Question",
        colIntent: "Intent",
        colLead: "Lead",
        colStatus: "Status",
        colReceived: "Received",
        anon: "Anonymous lead",
        question: "Question",
        assignedTo: "Assigned to",
        unassigned: "Unassigned",
        me: "Me",
        notes: "Notes",
        notesPh: "Call notes, next steps, context…",
        saveNote: "Save note",
        replyEmail: "Reply by email",
        noteSaved: "Note saved",
        assigned: "Assigned",
        unassignedT: "Unassigned",
        statusUpdated: "Status updated",
        emailCopied: "Email copied",
        hasNotes: "Has notes",
        copyEmail: "Copy email",
      },
      conversations: {
        title: "Conversations",
        subtitle: "Every dialogue your twin has, in one thread.",
        searchPh: "Search conversations…",
        noMatch: "No conversations match your search.",
        empty: "No conversations yet. Real visitor and owner chats will appear here.",
        visitor: "Visitor",
        owner: "Owner",
        conversation: "Conversation",
        selectPrompt: "Select a conversation to review the transcript.",
      },
      requests: {
        title: "Service Requests",
        subtitle: "Customers from the public directory — quote, get paid online, do the job.",
        notListed:
          "You're not listed in the service directory yet — turn it on to receive paying customers.",
        enableSettings: "Enable in Settings",
        emptyTitle: "No requests yet",
        emptyBody:
          "Once customers find you in the directory and ask for a quote, everything lands here — quote it and get paid online before the job starts.",
        stAll: "All",
        stNew: "New",
        stQuoted: "Quoted",
        stPaid: "Paid",
        stCompleted: "Completed",
        stDeclined: "Declined",
        budget: "Budget",
        quotedLabel: "Quoted",
        yourPayout: "your payout",
        afterFee: "after {{pct}}% platform fee",
        updateQuote: "Update quote",
        sendQuote: "Send quote",
        markCompleted: "Mark completed",
        completedToast: "Marked as completed",
        declinedToast: "Declined",
        enterPrice: "Enter a price",
        quoteSent: "Quote sent — the customer received a secure payment link.",
        pricePh: "Price",
        notePh: "Note for the customer (scope, date, terms…)",
        send: "Send",
        sending: "Sending…",
        customerPays: "Customer pays",
        platformFee: "platform fee",
      },
      analytics: {
        revenueTitle: "Earned through Reality Twin · last {{days}} days",
        revenueNone: "No jobs have been paid through the platform yet.",
        revenueNet: "yours, after the {{fee}} platform fee",
        revenueJobs: "paid jobs",
        revenuePending:
          "{{n}} quote(s) sent and not yet paid — not counted above, because they are not revenue until they are.",
        title: "Analytics",
        subtitle: "Twin performance across conversations, messages and leads.",
        conversations: "Conversations",
        messages: "Messages exchanged",
        leads: "Leads captured",
        topQuestions: "Top questions from leads",
        noQuestions: "No lead questions yet.",
        gaps: "Knowledge gaps",
        gapsSub: "Questions your twin could not answer from its knowledge base",
        noGaps: "No open gaps — your twin had an answer for everything it was asked.",
        asked: "asked {{count}}× · last {{date}}",
        addKnowledge: "Add knowledge",
        covered: "Covered",
        coveredToast: "Marked as covered",
      },
      knowledge: {
        title: "Knowledge Center",
        subtitle: "Everything your AI Twin knows.",
        stSources: "Sources",
        stChunks: "Chunks indexed",
        stWebsites: "Websites",
        stDocuments: "Documents",
        add: "Add knowledge",
        tWebsite: "Website",
        tDocuments: "Documents",
        tText: "Paste text",
        urlPh: "https://your-company.com/about",
        crawlIndex: "Crawl & index",
        websiteHelp: "Fetches the page and adds its text to your twin's knowledge base.",
        uploadDocs: "Upload documents",
        uploadHint: "PDF, DOCX, TXT, MD, CSV, JSON or HTML · max 20MB each",
        extracting: "Extracting & indexing…",
        uploadIndex: "Upload, read & index",
        titlePh: "Title",
        contentPh: "Paste product info, FAQs, mission, sales scripts…",
        addToKb: "Add to knowledge base",
        sources: "Sources",
        searchPh: "Search…",
        loading: "Loading…",
        noSources: "No sources yet. Add one on the left.",
        removed: "Removed",
        indexedChunks: "Indexed {{n}} chunks",
        indexedDocs: "Indexed {{n}} documents. Your twin knows them now.",
        crawled: "Crawled {{pages}} pages ({{chunks}} chunks)",
        moreDiscovered: "{{n}} more pages discovered — run the crawl again to continue.",
      },
      exchange: {
        needFramework:
          "Before you can issue contracts through the network, the service distribution agreement has to be signed. It takes a minute and only happens once.",
        toContract: "Draft a contract",
        contractTitle: "Draft a contract",
        contractSub:
          "With {{name}}. Everything below is prefilled from the two listings and is yours to change — you are writing the terms, not confirming them.",
        scopeLabel: "What is being delivered",
        scopeTooShort: "Describe what is being delivered — at least a sentence.",
        amountLabel: "Price ({{cur}})",
        amountPh: "leave empty for time and materials",
        deliveryLabel: "Delivery date",
        paymentLabel: "Payment terms",
        paymentPh: "empty means 30 days net",
        contractNote:
          "Creating it signs it on your side and sends it to them to countersign. You can withdraw it until they sign.",
        createContract: "Create and sign",
        contractSent: "Contract created and signed by you — it now awaits the other side.",
        title: "Business Exchange",
        subtitle:
          "Concrete deals, not directory entries — sell, source, raise and invest across the network.",
        newListing: "New listing",
        myListings: "My listings",
        browseNetwork: "Browse network",
        pickerTitle: "What do you want to do?",
        pickerSub:
          "Every listing is embedded and matched against complementary listings across all industries.",
        titleLabel: "Title *",
        titlePh: "One line a counterpart immediately understands",
        descLabel: "Description",
        descPh: "The concrete facts a counterpart needs to say yes to a call.",
        industryLabel: "Industry",
        industryPh: "e.g. Consumer goods",
        countryLabel: "Country / region",
        countryPh: "e.g. Switzerland, EU",
        min: "(min)",
        max: "(max)",
        currencyLabel: "Currency",
        attachLabel: "Attachment (pitch deck, catalogue — PDF/image, max 10 MB)",
        chooseFile: "Choose file",
        publishing: "Publishing & matching…",
        publishFind: "Publish & find matches",
        emptyTitle: "Put a real deal on the network",
        emptyBody:
          "Sell a product, source suppliers, raise capital with your numbers, or define your investment ticket. Matching counterparts are found and notified automatically.",
        createFirst: "Create your first listing",
        matchesCount: "Matches ({{count}})",
        matchBtn: "Match",
        pause: "Pause",
        activate: "Activate",
        close: "Close",
        inbound: "inbound",
        talkTwin: "Talk to twin",
        contacted: "Contacted",
        all: "All",
        industryFilter: "Industry",
        countryFilter: "Country",
        searchListings: "Search listings…",
        noBrowse: "No listings match. Post one — counterparts get notified the moment they list.",
        talkCompanyTwin: "Talk to this company's twin",
        liveMatches: "Listing live — {{count}} matches found and notified.",
        liveNone: "Listing live. The network is watching — matches appear as counterparts post.",
        matchesFound: "{{count}} matches found",
        couldNotCreate: "Could not create listing",
        attachTooBig: "Attachment must be under 10 MB",
        signInAgain: "Please sign in again",
        fromAmt: "from",
        upToAmt: "up to",
        stActive: "active",
        stPaused: "paused",
        stClosed: "closed",
        types: {
          product_offer: {
            label: "Sell a product",
            tagline: "Find retailers, distributors and buyers for your product.",
            amountLabel: "Unit price range",
            f: {
              category: "Product category",
              moq: "Minimum order",
              margin: "Reseller margin",
              certifications: "Certifications",
            },
            p: {
              category: "e.g. Organic skincare",
              moq: "e.g. 100 units",
              margin: "e.g. 40%",
              certifications: "e.g. CE, BIO, ISO 9001",
            },
          },
          product_demand: {
            label: "Source products",
            tagline: "You run a store or need products for your segment.",
            amountLabel: "Purchase budget",
            f: {
              category: "Category wanted",
              volume: "Purchase volume",
              segment: "Customer segment",
            },
            p: {
              category: "e.g. Sustainable home goods",
              volume: "e.g. 500 units / month",
              segment: "e.g. Urban 25-40, premium",
            },
          },
          invest_ask: {
            label: "Raise capital",
            tagline: "Present your venture with real numbers to matching investors.",
            amountLabel: "Funding ask",
            f: {
              stage: "Stage",
              revenue: "Annual revenue",
              growth: "Growth (YoY)",
              valuation: "Valuation",
              use_of_funds: "Use of funds",
            },
            p: {
              stage: "e.g. Seed",
              revenue: "e.g. 250k USD ARR",
              growth: "e.g. 180%",
              valuation: "e.g. 4M USD",
              use_of_funds: "e.g. Sales team + product",
            },
          },
          invest_offer: {
            label: "Invest capital",
            tagline: "Define your ticket, stages and sectors — deals find you.",
            amountLabel: "Ticket size",
            f: {
              stages: "Preferred stages",
              sectors: "Sectors",
              geography: "Geography",
            },
            p: {
              stages: "e.g. Pre-Seed to Series A",
              sectors: "e.g. SaaS, Fintech, Climate",
              geography: "e.g. DACH, EU",
            },
          },
          service_offer: {
            label: "Offer a service",
            tagline: "Put your capacity in front of companies that need it.",
            amountLabel: "Typical project size",
            f: {
              category: "Service category",
              capacity: "Availability",
            },
            p: {
              category: "e.g. Performance marketing",
              capacity: "e.g. 2 projects from August",
            },
          },
          service_demand: {
            label: "Find a supplier or service",
            tagline: "Describe what you need — matching providers are notified.",
            amountLabel: "Budget",
            f: {
              category: "What you need",
              timeline: "Timeline",
            },
            p: {
              category: "e.g. Logistics partner for e-commerce",
              timeline: "e.g. Start within 4 weeks",
            },
          },
          partnership: {
            label: "Strategic partnership",
            tagline: "Co-marketing, integrations, alliances — find companies that complement you.",
            amountLabel: "Deal size (optional)",
            f: {
              focus: "Partnership focus",
              bring: "What you bring",
              seek: "What you look for",
            },
            p: {
              focus: "e.g. Co-marketing, tech integration",
              bring: "e.g. 40k newsletter reach, API platform",
              seek: "e.g. Complementary SaaS with SMB base",
            },
          },
          license_offer: {
            label: "License out IP",
            tagline: "Monetize patents, brands, software or content through licensing.",
            amountLabel: "License fee range",
            f: {
              asset: "Asset type",
              territory: "Territory",
              model: "Royalty model",
            },
            p: {
              asset: "e.g. Patent, brand, white-label software",
              territory: "e.g. DACH exclusive, worldwide",
              model: "e.g. 5% rev-share, flat fee",
            },
          },
          license_demand: {
            label: "Find technology / IP",
            tagline: "Looking for white-label tech, patents or brands to license in.",
            amountLabel: "Budget",
            f: {
              seeking: "What you need",
              territory: "Territory",
            },
            p: {
              seeking: "e.g. White-label booking software",
              territory: "e.g. EU rights",
            },
          },
          business_sale: {
            label: "Sell a business (M&A)",
            tagline: "Present your company to matching acquirers — with real numbers.",
            amountLabel: "Asking price",
            f: {
              revenue: "Annual revenue",
              ebitda: "EBITDA",
              employees: "Employees",
              reason: "Reason for sale",
            },
            p: {
              revenue: "e.g. 1.2M CHF",
              ebitda: "e.g. 300k CHF",
              employees: "e.g. 8",
              reason: "e.g. Succession, new venture",
            },
          },
          business_buy: {
            label: "Acquire a business",
            tagline: "Define your target profile — sellers find you before brokers do.",
            amountLabel: "Acquisition budget",
            f: {
              target: "Target profile",
              industries: "Industries",
              geography: "Geography",
            },
            p: {
              target: "e.g. SaaS, 0.5-5M ARR, profitable",
              industries: "e.g. Software, e-commerce",
              geography: "e.g. DACH, remote-first",
            },
          },
        },
      },
      opportunities: {
        reachThin: "The network is still small — {{n}} other companies can currently be matched.",
        reachThinBody:
          "Matching ranks and checks candidates — it cannot create them. With this few counterparts an empty result is the honest one, not a fault in the engine. Every company that joins and describes what it does widens the pool for everyone.",
        reachOk: "{{n}} companies are currently matchable.",
        reachOkBody:
          "Of those, {{needs}} have written down what they are looking for — the strongest signal the network carries.",
        reachPublic: "public",
        reachSearchable: "matchable",
        reachNeeds: "with stated needs",
        reachListing: "Post what you are looking for",
        reachBrowse: "Browse who is here",
        briefTitle: "Brief your twin",
        briefSub:
          "Say what you want to establish with {{name}}. Your twin drives the conversation toward it, and the analyst reports whether it got there.",
        briefPlaceholder: "e.g. Find out whether they can machine titanium parts under 50mm",
        briefOptional: "Optional — but without one the meeting has nothing to aim at.",
        objectiveLabel: "Objective for this meeting",
        objSupplier: "Find out whether they could supply us, and on what terms",
        objResell: "Explore whether they would resell or refer our services",
        objCapacity: "Check whether they have capacity for a project in the next quarter",
        dec_proceed: "Proceed",
        dec_park: "Park for now",
        dec_no_fit: "No fit",
        fitLabel: "Fit",
        whyNow: "Why now",
        howScored: "How this score was built",
        kSupplier: "Supplier",
        dirMutual: "Mutual fit",
        dirInbound: "They need us",
        dirOutbound: "We need them",
        reciprocityTitle: "How strongly they would want this: {{n}}/100",
        ev_counterpart_profile: "their profile",
        ev_counterpart_seeking: "what they are seeking",
        ev_our_seeking: "what we are seeking",
        ev_signal: "checked signal",
        sig_need: "Covers our need",
        sig_offer: "We cover theirs",
        sig_semantic: "Profile similarity",
        sig_industry: "Industry fit",
        sig_geography: "Same market",
        sig_trust: "Counterpart trust",
        sig_activity: "Recent activity",
        ost_reviewing: "Shortlisted",
        ost_contacted: "Contacted",
        ost_accepted: "Won",
        ost_dismissed: "Dismissed",
        markReviewing: "Shortlist",
        markAccepted: "Won",
        dismissTitle: "Not relevant — never show this company again",
        dismissedForGood: "Dismissed — this company will not be suggested again.",
        generatedNone:
          "No new fits held up under checking. Nothing was added — that is the honest answer.",
        title: "Opportunity Engine",
        subtitle:
          "AI-matched partners, customers, and investors — let the twins take the first meeting.",
        matching: "Matching…",
        generateMatches: "Generate matches",
        loading: "Loading…",
        noOppsTitle: "No opportunities yet.",
        noOppsBefore: "Publish your twin in the ",
        marketplace: "marketplace",
        noOppsAfter: ' and click "Generate matches".',
        kPartner: "partner",
        kCustomer: "customer",
        kInvestor: "investor",
        confidence: "Confidence {{n}}%",
        startMeeting: "Start twin meeting",
        twinMeetings: "Twin Meetings",
        twinMeetingsSub:
          "Your twin's meetings across the network — transcript and verdict included.",
        noMeetings:
          "No meetings yet. Start one from a match above — your twin takes the first meeting for you.",
        transcript: "Transcript",
        analystVerdict: "Analyst verdict",
        oppsLabel: "Opportunities",
        openQuestions: "Open questions",
        recommendedNext: "Recommended next step",
        draftIntro: "Draft intro message",
        copy: "Copy",
        synergy: "synergy",
        meetingFallback: "Meeting transcript available",
        phase0: "Twins are connecting…",
        phase1: "Exchanging company knowledge…",
        phase2: "Exploring commercial fit…",
        phase3: "Negotiating next steps…",
        phase4: "Analyst is writing the verdict…",
        generated: "Generated {{n}} matches",
        meetingCompleted: "Meeting with {{name}} completed",
        meetingFailed: "Meeting failed",
        introCopied: "Intro message copied",
      },
      settings: {
        movedTitle: "Company profile, verification and payouts are on your AI Twin",
        movedSub:
          "Logo, company details, domain verification, service offering and payout account are all managed there.",
        title: "Settings",
        subtitle: "Company profile, notifications and account.",
        tabCompany: "Company",
        tabTrust: "Trust",
        tabTeam: "Team",
        tabIntegrations: "Integrations",
        tabNotifications: "Notifications",
        tabSecurity: "Security",
        companyProfile: "Company profile",
        companyProfileSub: "This is how your twin appears across the network and marketplace.",
        companyLogo: "Company logo",
        logoHint: "PNG, JPG, WebP or SVG · max 2 MB",
        uploading: "Uploading…",
        replace: "Replace",
        uploadLogo: "Upload logo",
        remove: "Remove",
        fName: "Company name",
        fWebsite: "Website",
        fIndustry: "Industry",
        fCountry: "Country",
        fSize: "Size",
        fDescription: "Description",
        publishTwin: "Publish twin in Marketplace",
        publishTwinSub: "Anyone can discover and chat with your twin.",
        listServices: "List in the service directory",
        listServicesSub:
          "Customers request quotes and pay you online — 10% platform fee per paid job.",
        yourServiceCats: "Your service categories",
        saved: "Saved",
        logoTooBig: "Logo must be under 2 MB",
        logoFormat: "Use PNG, JPG, WebP or SVG",
        notSignedIn: "Not signed in",
        logoUploaded: "Logo uploaded",
        logoRemoved: "Logo removed",
        businessDetails: "Business details",
        businessDetailsSub:
          "The legal block shown on every quote and invoice your customers receive. Required before you can send paid service quotes.",
        legalName: "Legal company name",
        vat: "VAT / UID (optional)",
        street: "Street & number",
        zip: "ZIP",
        city: "City",
        phone: "Phone (optional)",
        payoutAccount: "Payout account",
        payoutAccountSub:
          "Where your earnings are paid out. Stored in a private table, encrypted at rest, and never shown to customers or other companies.",
        youKeepBefore: "You keep ",
        youKeepAfter: " of every paid job",
        platformFee10: "10% platform fee",
        payoutIban: "Payout IBAN",
        accountHolder: "Account holder",
        bankPrivate:
          "Bank details are private and never displayed on your public profile or quotes.",
        saveBusiness: "Save business details",
        profileSaved: "Business & payout details saved",
        teamMembers: "Team members",
        teamSub:
          "Teammates share this workspace — twin, conversations, leads, knowledge and exchange. Only the owner can invite or remove people.",
        seatsActive: "{{count}} seats active",
        noTeammates: "No teammates yet — invite your first colleague below.",
        pendingInvites: "Pending invitations",
        invited: "invited {{date}}",
        memberRemoved: "Member removed",
        emailPh: "colleague@company.com",
        roleMember: "Member",
        roleAdmin: "Admin",
        sending: "Sending…",
        sendInvite: "Send invite",
        inviteSent: "Invitation sent to {{email}}",
        revokeTitle: "Revoke invite",
        leadWebhook: "Lead webhook (CRM)",
        webhookDescBefore:
          "Every lead your twin captures is POSTed as JSON to this URL — connect HubSpot, Pipedrive, Zapier, Make or your own endpoint. Requests carry an ",
        webhookDescAfter: " header (HMAC-SHA256 of the body with your secret).",
        webhookUrl: "Webhook URL",
        signingSecret: "Signing secret",
        copySecretTitle: "Copy secret",
        genSecretTitle: "Generate new secret",
        saveWebhook: "Save webhook",
        sendTest: "Send test event",
        disable: "Disable",
        newSecret: "New secret generated",
        webhookSaved: "Webhook saved",
        secretCopied: "Secret copied",
        testDelivered: "Test delivered (HTTP {{status}}) — check your endpoint.",
        emailNotifs: "Email notifications",
        emailNotifsSub: "Choose what lands in your inbox. You can change this any time.",
        leadAlerts: "New lead alerts",
        leadAlertsSub: "Email me the moment a hot lead is captured.",
        weeklyDigest: "Weekly digest",
        weeklyDigestSub: "A summary of twin activity every Monday.",
        productUpdates: "Product updates",
        productUpdatesSub: "Occasional news about new features.",
        savePrefs: "Save preferences",
        notifsUpdated: "Notifications updated",
        passwordTitle: "Password",
        passwordSub: "Use at least 8 characters. Choose something you don't use elsewhere.",
        newPassword: "New password",
        passwordPh: "Min 8 characters",
        updatePassword: "Update password",
        activeSessions: "Active sessions",
        activeSessionsSub:
          "Signs you out on every device and browser. You'll need to sign in again everywhere.",
        signOutAll: "Sign out all sessions",
        pwTooShort: "Password must be at least 8 characters",
        pwUpdated: "Password updated",
        roleOwner: "Owner",
        workspaceOwner: "Workspace owner",
        removeMemberTitle: "Remove member",
        you: "You",
      },
      contracts: {
        title: "Contracts",
        subtitle: "Your distribution agreement and every contract you sign in the network.",
        contract: "Contract",
        close: "Close",
        engagements: "Service contracts",
        empty:
          "No service contracts yet. Once you agree terms with another company, the contract appears here for both sides to sign.",
        asProvider: "you deliver",
        asCustomer: "you receive",
        sigCount: "{{n}}/2 signed",
        st_draft: "Draft",
        st_sent: "Awaiting signature",
        st_signed: "Signed",
        st_declined: "Declined",
        st_cancelled: "Withdrawn",
        role_provider: "Provider",
        role_customer: "Client",
        consent:
          "I have read the contract above and sign it as a legally binding declaration on behalf of my company.",
        signerName: "Full name of the signatory",
        signerPh: "Firstname Lastname",
        signCta: "Sign contract",
        signing: "Signing…",
        signed: "Contract signed",
        youSigned: "You signed this contract on {{date}}.",
        evidenceNote:
          "Name, time, IP address, browser and a SHA-256 checksum of the contract text are recorded as proof of signature.",
        hashNote: "— the fingerprint your signature is bound to",
        withdraw: "Withdraw this contract",
        withdrawn: "Contract withdrawn",
        frameworkTitle: "Service distribution agreement",
        frameworkSub:
          "Sign once to list services in the network. It sets the brokerage fee, payouts and who is liable for what — the platform brokers, you deliver.",
        frameworkSigned: "Agreement signed — you can now offer services.",
        signFramework: "Sign agreement",
        signedBy: "Signed by {{name}} on {{date}}",
        showText: "Read the contract",
        hideText: "Hide contract text",
      },
      payouts: {
        title: "Your payouts",
        subtitle:
          "Customers pay into Reality Twin, and your share is transferred to your account by bank. This is what is owed and what has gone out.",
        owed: "Owed to you",
        paidOut: "Paid out",
        grossFee: "{{gross}} minus {{fee}} fee",
        blockedWarning:
          "We have no payout account for you, so {{n}} payment(s) cannot be sent. Add your IBAN above and they will be released.",
        st_owed: "Owed",
        st_blocked: "Needs your IBAN",
        st_paid: "Paid",
        st_failed: "Transfer failed",
        adminOwed: "Owed to providers",
        adminOpen: "To pay",
        adminHistory: "Settled",
        adminBlocked: "{{n}} blocked — the provider has no IBAN on file",
        nothingOwed: "Nothing is currently owed.",
        thProvider: "Provider",
        thAmount: "Net payout",
        thAccount: "Account",
        thStatus: "Status",
        markPaid: "Mark paid",
        confirmPaid: "Confirm",
        refPh: "bank reference",
        needReference: "Enter the bank reference — a payout without proof is not a record.",
        marked: "Marked as paid",
        ibanCopied: "IBAN copied",
        noIban: "no IBAN",
      },
      twin: {
        offerServicesLocked: "Sign the service distribution agreement above to list your services.",
        tabTrust: "Trust",
        tabServices: "Services",
        offerServicesLabel: "Offer services in the marketplace",
        offerServicesHelp:
          "Other companies' agents can find you and request a quote. You confirm every engagement yourself.",
        createProfilePrompt: "Create your company profile to activate your twin.",
        startOnboarding: "Start onboarding",
        subtitle: "Conversational interface for {{name}}",
        tabChat: "Chat",
        tabProfile: "Company profile",
        tabPersonality: "Personality",
        twinStatus: "Twin {{status}}",
        noPersonality: "No personality set. Generate one from your knowledge.",
        knowledgeBase: "Knowledge base",
        kbDocuments: "Documents",
        kbSize: "Knowledge size",
        kbAiScore: "AI score",
        updateKnowledge: "Update knowledge",
        recentConversations: "Recent conversations",
        noConversations: "No conversations yet.",
        conversationFallback: "Conversation",
        viewAll: "View all →",
        interactiveChat: "Interactive AI Chat",
        interactiveChatSub:
          "Answers from your indexed knowledge · cited sources · saved to Conversations.",
        starter1: "What does our company do?",
        starter2: "Who is our ideal customer?",
        starter3: "What partnerships should we pursue?",
        chatPlaceholder: "Ask your twin…",
        profileTitle: "Company profile",
        profileSub:
          "This is what your twin knows about the company and what visitors see on your public profile. The description can be written for you by AI from your indexed knowledge.",
        descLabel: "Description",
        writing: "Writing…",
        generateAI: "Generate with AI",
        descPlaceholder:
          "Describe your company — or let AI write it from your indexed website & documents.",
        saveProfileBtn: "Save company profile",
        personalityTitle: "Twin personality (system prompt)",
        personalitySub: "This defines your twin's voice, expertise and tone.",
        personalityPlaceholder:
          "Define how your AI Twin should introduce the company, answer questions and hand off qualified leads.",
        savePersonalityBtn: "Save personality",
        generating: "Generating…",
        regenerateBtn: "Regenerate from knowledge",
        installTitle: "Install",
        embedTitle: "Embed on your website",
        makePublicBefore: "Make your Twin public in ",
        settingsLink: "Settings",
        makePublicAfter: " to enable the widget.",
        pasteBefore: "Paste this snippet right before ",
        pasteAfter: ". A floating chat bubble appears with your Twin.",
        copySnippet: "Copy snippet",
        preview: "Preview",
        personalitySaved: "Personality saved",
        saveFailed: "Save failed",
        personalityRegen: "Personality regenerated from knowledge",
        regenFailed: "Regenerate failed",
        profileSaved: "Company profile saved",
        descWritten: "Description written by AI from your knowledge",
        couldNotGenerate: "Could not generate",
        snippetCopied: "Snippet copied to clipboard",
        couldNotCopy: "Could not copy",
      },
      appointments: {
        title: "Appointments",
        subtitle: "Schedule meetings with leads and customers — add them to any calendar.",
        newAppointment: "New appointment",
        titlePh: "Title (e.g. Discovery call with Acme)",
        contactNamePh: "Contact name",
        contactEmailPh: "Contact email",
        dateTime: "Date & time",
        duration: "Duration (min)",
        locationPh: "Location or video link (optional)",
        notesPh: "Notes (optional)",
        scheduling: "Scheduling…",
        schedule: "Schedule",
        noAppointments: "No appointments yet",
        noAppointmentsSub:
          "Schedule a meeting with a lead or customer. Every appointment gets a Google Calendar link and an .ics download.",
        upcoming: "Upcoming",
        pastCancelled: "Past & cancelled",
        statusCompleted: "completed",
        statusCancelled: "cancelled",
        googleCalendar: "Google Calendar",
        done: "Done",
        cancelAppt: "Cancel",
        deleteTitle: "Delete",
        deleteConfirm: "Delete this appointment?",
        scheduled: "Appointment scheduled",
        couldNotSchedule: "Could not schedule",
        failed: "Failed",
      },
      help: {
        title: "Help & Support",
        subtitle: "Open a ticket and track replies from our team.",
        newTicket: "New ticket",
        newTicketForm: "New support ticket",
        subjectPh: "Subject",
        catGeneral: "General question",
        catBilling: "Billing",
        catTechnical: "Technical issue",
        catFeature: "Feature request",
        catAccount: "Account",
        bodyPh: "Describe your question or issue…",
        sendTicket: "Send ticket",
        allTickets: "All tickets",
        you: "You",
        supportTeam: "Reality Twin team",
        replyPh: "Write a reply…",
        noTickets: "No tickets yet",
        noTicketsSub:
          "Have a question or ran into an issue? Open a ticket and our team will reply here.",
        updated: "updated {{time}}",
        stOpen: "Open",
        stPending: "Awaiting you",
        stResolved: "Resolved",
        stClosed: "Closed",
        justNow: "just now",
        minAgo: "{{n}}m ago",
        hourAgo: "{{n}}h ago",
        dayAgo: "{{n}}d ago",
        couldNotOpen: "Could not open ticket",
        ticketCreated: "Ticket created — we'll get back to you.",
        couldNotCreate: "Could not create ticket",
        couldNotSend: "Could not send",
      },
      billing: {
        title: "Billing",
        subtitle: "Plans and your shared conversation pool.",
        pastDueTitle: "Payment failed — update your card to keep your plan",
        pastDueBody:
          "We couldn't charge your card for the latest invoice. You still have full access during a short grace period while Stripe retries. Update your payment method to avoid interruption.",
        updatePayment: "Update payment method",
        currentPlan: "Current plan",
        cancelsOn: "Cancels on {{date}}",
        convThisMonth: "Conversations this month",
        sharedAcrossN: "Shared across your {{count}} twins",
        sharedAcrossAll: "Shared across all your twins",
        resetsSuffix: " · resets {{date}}",
        poolWarning:
          "You've used {{pct}}% of your pool. Upgrade your plan below for a bigger monthly allowance across all your twins.",
        manageSubscription: "Manage subscription, invoices & payment method →",
        monthly: "Monthly",
        yearly: "Yearly · save 2 mo",
        current: "Current",
        contact: "Contact",
        perYr: "/yr",
        perMo: "/mo",
        active: "Active",
        contactSales: "Contact sales",
        switching: "Switching…",
        switchPlan: "Switch to this plan",
        upgrade: "Upgrade",
        checkout: "Checkout",
        close: "Close",
        entTitle: "Talk to Enterprise Sales",
        entSub: "Custom pricing, SSO, DPA and dedicated onboarding.",
        namePh: "Your name",
        emailPh: "Work email",
        companyPh: "Company",
        messagePh: "Tell us about your use case (team size, expected volume, integrations)…",
        sending: "Sending…",
        requestCall: "Request a call",
        inquiryReceived: "Message received. Our team will be in touch within 1 business day.",
        couldNotOpenPortal: "Could not open subscription portal",
        signInFirst: "Please sign in first",
        switchedTo: "Switched to {{plan}}. Your invoice reflects proration.",
        couldNotChangePlan: "Could not change plan",
        plans: {
          free: {
            limit: "50 conversations / mo",
            features: ["1 twin", "Shared conversation pool", "Community support"],
          },
          starter: {
            limit: "1,000 conversations / mo",
            features: [
              "Up to 3 twins",
              "One shared conversation pool",
              "Lead capture & scoring",
              "Buy extra conversations",
              "Email support",
            ],
          },
          professional: {
            limit: "5,000 conversations / mo",
            features: [
              "Unlimited twins",
              "One shared conversation pool",
              "Twin-to-Twin protocol",
              "Opportunity engine",
              "Priority support",
            ],
          },
          scale: {
            limit: "20,000 conversations / mo",
            features: [
              "Everything in Pro",
              "Highest conversation volume",
              "Buy extra conversations",
              "Priority support",
            ],
          },
        },
      },
      onboarding: {
        stepOf: "Onboarding · Step {{step}} of {{total}}",
        buildingTitle: "Building your AI Twin",
        buildTitle: "Let's build your AI Twin",
        preparingTitle: "Preparing your workspace",
        preparingSub:
          "Crawling pages, structuring knowledge, calibrating tone and preparing handoff rules.",
        q0: "What's the name of your company?",
        phCompanyName: "Company name",
        q1: "Where should your twin learn from?",
        phWebsite: "https://your-company.com",
        lblIndustry: "Industry",
        phCountry: "Country / main market",
        q2: "What should your AI Twin understand and achieve?",
        phAudience: "Target audience",
        phDescription:
          "Describe your offer, services, positioning and what makes the company different.",
        q3: "How should your twin speak and hand off leads?",
        phHandoff: "When should the twin ask for email, book a call, or escalate to your team?",
        q4: "Add files your twin should know about.",
        uploadText: "Upload PDFs, Word docs, FAQs or service docs",
        uploadHint: "Optional — your website and form answers are enough to start.",
        q5: "Review your AI Twin setup.",
        sumCompany: "Company",
        sumWebsite: "Website",
        sumIndustry: "Industry",
        sumMarket: "Market",
        sumGoals: "Goals",
        sumTone: "Tone",
        toneSummary: "{{tone}} · {{count}} files",
        notProvided: "Not provided",
        back: "Back",
        continueBtn: "Continue",
        generate: "Generate",
        stepCompanyName: "Company name",
        stepWebsite: "Website & market",
        stepOffer: "Offer & audience",
        stepBehavior: "Twin behavior",
        stepKnowledge: "Knowledge",
        stepReview: "Review",
        signInAgain: "Please sign in again.",
        websiteWarn: "Website could not be crawled automatically. You can retry from Knowledge.",
        couldNotRead: "Could not read: {{files}}. Retry from the Knowledge Center.",
        twinReady: "Your AI Twin is ready",
        handoffDefault: "Book a call or collect name, email, company and request.",
        indSaaS: "SaaS",
        indAgency: "Agency",
        indEcommerce: "E-commerce",
        indConsulting: "Consulting",
        indFinance: "Finance",
        indHealthcare: "Healthcare",
        indRealEstate: "Real estate",
        indOther: "Other",
        goalQualify: "Qualify leads",
        goalSupport: "Answer support",
        goalBook: "Book calls",
        goalCapture: "Capture requests",
        goalExplain: "Explain services",
        goalRecommend: "Recommend products",
        tonePrecise: "Precise",
        toneWarm: "Warm",
        tonePremium: "Premium",
        toneDirect: "Direct",
      },
      admin: {
        tabPayouts: "Payouts",
        dTitle: "Customer record",
        dClose: "Close",
        dLoading: "Loading record…",
        dOpenHint: "Click to open record",
        dTwinCount: "{{n}} twins",
        roleAdminBadge: "Admin",
        saved: "Saved",
        dAccount: "Account",
        dUserId: "User ID",
        dProvider: "Sign-in method",
        dJoined: "Joined",
        dEmailConfirmed: "Email confirmed",
        dNotConfirmed: "not confirmed",
        dLastSignIn: "Last sign-in",
        dConsent: "Terms accepted",
        dNoConsent: "no consent record",
        dSubscription: "Subscription",
        dNoSubscription: "No Stripe subscription on file.",
        dPeriodEnd: "Current period ends",
        dCancelling: "Cancels at period end",
        dTwins: "Twins",
        dNoTwins: "This account has no twin yet.",
        dConversations: "Conversations",
        dLeads: "Leads",
        dDocuments: "Documents",
        dStatements: "Statements",
        dAppointments: "Appointments",
        dDeals: "Deals",
        dVerification: "Verification",
        dServicesOn: "In marketplace",
        dServicesOff: "Not listed",
        dBilling: "Business & payout",
        dNoBilling: "No business details entered.",
        dNoPayout: "No payout account",
        dTeam: "Team",
        dNoTeam: "No team members or invites.",
        dInvitePending: "invite pending",
        dTickets: "Support tickets",
        dNoTickets: "No support tickets.",
        dApplications: "Beta applications",
        dDelete: "Delete this twin",
        dDeleteWarn:
          "This permanently deletes the twin and everything attached to it — conversations, leads, documents and statements. Type the twin's name to confirm.",
        dDeleteConfirm: "Delete permanently",
        dDeleted: "Twin deleted",
        dCancel: "Cancel",
        thConsent: "Terms",
        consentYes: "Accepted",
        consentNone: "no record",
        consentNoneHint: "No record — this account was created before consent was captured.",
        loadingConsole: "Loading admin console…",
        consoleTitle: "Admin Console",
        errorSub: "Something went wrong",
        consoleSubtitle: "Full platform oversight — users, workspaces, plans and access",
        actAsUser: "Act as normal user",
        refresh: "Refresh",
        tabOverview: "Overview",
        tabUsers: "Users",
        tabWorkspaces: "Workspaces",
        tabRequests: "Requests",
        tabSupport: "Support",
        searchUsers: "Search users by email or company…",
        searchWorkspaces: "Search workspaces…",
        statUsers: "Registered users",
        statWorkspaces: "Workspaces",
        statMrr: "MRR (est.)",
        statNewWeek: "New this week",
        statPublic: "Public twins",
        statAdmins: "Admins",
        statSuspended: "Suspended",
        statConversations: "Conversations",
        planDistribution: "Plan distribution",
        planDistSub: "Per account — a user's twins share one subscription.",
        thUser: "User",
        thWorkspace: "Workspace",
        thPlan: "Plan",
        thJoined: "Joined",
        thLastActive: "Last active",
        thStatus: "Status",
        thActions: "Actions",
        noUsers: "No users match your search.",
        moreTwins: "+{{n}} more",
        suspended: "Suspended",
        active: "Active",
        revokeAdmin: "Revoke admin",
        makeAdmin: "Make admin",
        reactivate: "Reactivate",
        suspend: "Suspend",
        thCompany: "Company",
        thOwner: "Owner",
        thAiScore: "AI score",
        thVisibility: "Visibility",
        thCreated: "Created",
        noWorkspaces: "No workspaces match your search.",
        publicTitle: "Public — click to hide",
        hiddenTitle: "Hidden — click to publish",
        publicLabel: "Public",
        hiddenLabel: "Hidden",
        offersServices: "Offers services",
        thUsersExpected: "Users expected",
        thMotivation: "Motivation",
        noRequests: "No workspace requests yet.",
        approve: "Approve",
        reject: "Reject",
        justNow: "just now",
        minAgo: "{{n}}m ago",
        hourAgo: "{{n}}h ago",
        dayAgo: "{{n}}d ago",
        switchedUserView: "Switched to user view",
        failed: "Failed",
        grantedAdmin: "Granted admin",
        revokedAdmin: "Revoked admin",
        planSet: "Plan set to {{plan}}",
        userSuspended: "User suspended",
        userReactivated: "User reactivated",
        workspacePublished: "Workspace published",
        workspaceHidden: "Workspace hidden",
        ws_approved: "Workspace approved",
        ws_rejected: "Workspace rejected",
        ws_pending: "Workspace set to pending",
        st_pending: "pending",
        st_approved: "approved",
        st_rejected: "rejected",
      },
      twinChat: {
        phone: "Phone",
        company: "Company",
        address: "Street & number",
        postal: "ZIP",
        city: "City",
        country: "Country",
        addAddress: "+ Add full address",
        couldNotSend: "Could not send — please try again.",
        dismiss: "Dismiss",
        followUpQ: "Want {{company}} to follow up personally?",
        yourName: "Your name",
        workEmail: "Work email",
        send: "Send",
        contactShared: "Contact shared — {{company}} will follow up.",
        askPlaceholder: "Ask {{company}}…",
        willGetBack: "{{company}} will get back to you.",
      },
      adminSupport: {
        allTickets: "All tickets",
        thSubject: "Subject",
        thCustomer: "Customer",
        thCategory: "Category",
        thStatus: "Status",
        thUpdated: "Updated",
        loadingTickets: "Loading tickets…",
        noTickets: "No support tickets yet.",
        replyPlaceholder: "Write a reply to the customer…",
        reply: "Reply",
        youTeam: "You (team)",
        customer: "Customer",
        couldNotOpen: "Could not open",
        couldNotSend: "Could not send",
        stOpen: "Open",
        stPending: "Pending",
        stResolved: "Resolved",
        stClosed: "Closed",
        justNow: "just now",
        minAgo: "{{n}}m ago",
        hourAgo: "{{n}}h ago",
        dayAgo: "{{n}}d ago",
      },
      cmd: {
        searchPh: "Search or jump to… (leads, conversations, deals)",
        noResults: "No results.",
        hLeads: "Leads",
        hConversations: "Conversations",
        hListings: "Exchange listings",
        hActions: "Quick actions",
        hGoTo: "Go to",
        leadFallback: "Lead",
        convFallback: "Conversation",
        postDeal: "Post a deal on the Exchange",
        inviteTeammate: "Invite a teammate",
        copyLink: "Copy public twin link",
        linkCopied: "Public twin link copied",
        couldNotCopy: "Could not copy",
      },
      radar: {
        trig_stated_need: "Stated a need",
        trig_new_listing: "New listing",
        trig_new_counterpart: "Joined the network",
        trig_became_verified: "Now verified",
        trig_knowledge_gap: "Your twin cannot answer this",
        trig_stalled_opportunity: "Waiting on you",
        writeDraft: "Write outreach",
        drafting: "Writing…",
        showDraft: "Show draft",
        hideDraft: "Hide draft",
        fixGap: "Close this gap",
        reopen: "Pick it back up",
        title: "Deal Radar",
        subtitle: "Warm deals your twin found overnight — outreach already drafted.",
        refresh: "Scan now",
        scanning: "Scanning…",
        empty: "No new signals yet. Your twin scans the network daily — or scan now.",
        draftLabel: "Draft your twin wrote",
        copySend: "Copy & mark sent",
        dismiss: "Dismiss",
        openTwin: "Open their twin",
        fit: "{{score}}% fit",
        copied: "Draft copied — paste it to {{name}}",
        scanned: "Scan complete — {{count}} new signals",
        scannedNone: "Scan complete — no new signals right now",
      },
      trust: {
        title: "Trust & verification",
        subtitle:
          "Signals verified from real activity — not self-reported. Shown on your public twin.",
        scoreLabel: "Trust score",
        verifiedTwin: "Verified twin",
        refresh: "Refresh",
        refreshing: "Checking…",
        none: "No verified signals yet. Index your website, handle conversations and add payout details to build trust.",
        website: "Website verified",
        websiteDetail: "{{pages}} pages crawled & indexed",
        knowledge: "Knowledge base",
        knowledgeDetail: "{{docs}} documents indexed",
        active: "Active on the network",
        activeDetail: "{{conversations}} conversations handled",
        meetings: "Twin meetings",
        meetingsDetail: "{{meetings}} completed",
        payout: "Business & payout on file",
        payoutDetail: "{{country}}",
        vat: "VAT ID registered",
        vatDetail: "",
        established: "Established",
        establishedDetail: "on RTN since {{date}}",
      },
      deals: {
        openValue: "Open pipeline",
        wonValue: "Completed",
        mixedCurrency:
          "Deals span several currencies, so no total is shown — a summed figure would be wrong.",
        frozenNote: "— terms locked at first signature; both signatures bind to this fingerprint",
        title: "Deals",
        subtitle: "Term sheets your twins drafted — from proposal to signed.",
        emptyTitle: "No deals yet",
        emptyBody:
          "Run a twin meeting on a strong match, then draft a deal from its verdict — your twin writes the term sheet.",
        toMeetings: "Go to Opportunities",
        with: "with {{name}}",
        terms: "Term sheet",
        send: "Send to counterpart",
        sent: "Deal sent",
        awaiting: "Awaiting their response",
        accept: "Accept",
        accepted: "Deal accepted",
        sign: "E-sign",
        signedToast: "Signed",
        markPaid: "Mark paid",
        paidToast: "Marked paid",
        markCompleted: "Mark completed",
        completedToast: "Deal completed",
        draftDeal: "Draft a deal",
        dealDrafted: "Deal drafted — open Deals",
        st_draft: "Draft",
        st_proposed: "Proposed",
        st_accepted: "Accepted",
        st_declined: "Declined",
        st_signed: "Signed",
        st_paid: "Paid",
        st_completed: "Completed",
      },
      crm: {
        saved: "Saved",
        deleted: "Customer deleted",
        newAppointment: "Schedule",
        deleteConfirm:
          "Delete this customer? Their leads and orders stay, but they're removed from your customer list.",
        details: "Details",
        edit: "Edit",
        delete: "Delete",
        save: "Save",
        noDetails: "No contact details yet — click Edit to add an address, phone and more.",
        fName: "Name",
        fPhone: "Phone",
        fCompany: "Company",
        fAddress: "Address",
        fCity: "City",
        fPostal: "Postal code",
        fCountry: "Country",
        fWebsite: "Website",
        fStatus: "Status",
        fEmail: "Email",
        addCustomer: "Add customer",
        addCta: "Add",
        viewCustomers: "Customers",
        viewPipeline: "Pipeline",
        search: "Search customers…",
        noCustomers:
          "No customers yet. They appear here as your twin captures conversations, leads, appointments and orders.",
        noMatch: "No customers match your search.",
        stCustomer: "Customer",
        stContact: "Contact",
        cConversations: "Conversations",
        cLeads: "Leads",
        cAppointments: "Appointments",
        cOrders: "Orders",
        since: "Customer since",
        lastSeen: "Last activity",
        scoreLbl: "Lead score",
        openValue: "Open value",
        secTimeline: "Activity",
        secNotes: "Notes",
        addNote: "Add note",
        notePlaceholder: "Write a note about this customer…",
        noteSaved: "Note added",
        noNotes: "No notes yet.",
        recordEmpty: "Nothing here yet.",
        kindConversation: "Conversation",
        kindLead: "Lead",
        kindAppointment: "Appointment",
        kindOrder: "Order",
        kindNote: "Note",
        title: "CRM & Orders",
        subtitle: "Every lead, order, deal and appointment in one pipeline.",
        kLeads: "Leads",
        kHot: "Hot leads",
        kOpenOrders: "Open orders",
        kRevenue: "Revenue",
        kPayout: "Your payout",
        kDeals: "Active deals",
        kPipeline: "Pipeline value",
        all: "All",
        fLead: "Leads",
        fOrder: "Orders",
        fDeal: "Deals",
        fAppt: "Appointments",
        empty:
          "Nothing here yet. Leads, orders, deals and appointments show up here as your twin works.",
        scheduled: "scheduled",
      },
    },
    auth: {
      passwordConfirm: "Confirm password",
      passwordMismatch: "The passwords don't match.",
      mustAcceptTerms: "Please accept the Terms and the Privacy Policy to continue.",
      acceptPre: "I accept the",
      acceptTerms: "Terms of Service",
      acceptAnd: "and the",
      acceptPrivacy: "Privacy Policy",
      signIn: "Sign in",
      signUp: "Create account",
      welcomeBack: "Welcome back.",
      buildTwin: "Build your AI Twin.",
      signInSub: "Sign in to your Reality Twin workspace.",
      signUpSub: "Start your AI Twin in a few minutes.",
      continueGoogle: "Continue with Google",
      or: "or",
      email: "Email",
      password: "Password",
      emailPlaceholder: "you@company.com",
      submitSignIn: "Sign in",
      submitSignUp: "Create account",
      newHere: "New to Reality Twin?",
      alreadyHave: "Already have an account?",
      switchToSignUp: "Create an account",
      switchToSignIn: "Sign in",
      welcomeToast: "Welcome to Reality Twin. Check your inbox to confirm.",
      confirmEmailSent: "Almost there — check your inbox to confirm your email, then sign in.",
      alreadyRegistered: "This email is already registered. Please sign in.",
      authFailed: "Authentication failed",
      googleFailed: "Google sign-in failed",
    },
    shell: {
      nav: {
        contracts: "Contracts",
        dashboard: "Dashboard",
        twin: "AI Twin",
        knowledge: "Knowledge",
        conversations: "Conversations",
        leads: "Leads",
        requests: "Requests",
        appointments: "Appointments",
        deals: "Deals",
        crm: "CRM",
        opportunities: "Opportunities",
        exchange: "Exchange",
        analytics: "Analytics",
        marketplace: "Marketplace",
        billing: "Billing",
        help: "Help & Support",
        settings: "Settings",
      },
      freePlan: "Free plan",
      convUsed: "43 / 50 conversations used",
      upgrade: "Upgrade",
      signOut: "Sign out",
      twinSwitcher: {
        label: "Active twin",
        couldNotSwitch: "Could not switch twin",
        created: "Twin created",
        couldNotCreate: "Could not create twin",
        couldNotDelete: "Could not delete twin",
        delete: "Delete twin",
        deleted: "Twin deleted",
        confirmDelete: 'Permanently delete "{{name}}" and all its data? This cannot be undone.',
        shared: "Shared with me",
        newTwin: "New twin",
        namePlaceholder: "New twin name",
        create: "Create",
        upgradeForMore: "Upgrade for more twins",
        limitReached: "Twin limit reached — upgrade for more",
      },
      notifications: {
        title: "Notifications",
        empty: "You're all caught up.",
        lead: "New lead",
        match: "New Exchange match",
        request: "New service request",
      },
    },
    dashboard: {
      planWord: "Plan",
      conversationsWord: "conversations",
      upgrade: "Upgrade",
      sharedAcrossTwins: "Shared across all your twins",
      statMessages: "Messages",
      subtitleConv_one: "Your twin handled {{n}} conversation in the last 30 days.",
      subtitleConv_other: "Your twin handled {{n}} conversations in the last 30 days.",
      subtitleEmpty:
        "Your twin is ready. Add knowledge and share your chat widget to start collecting real conversations.",
      shareTwin: "Share twin",
      linkCopied: "Public twin link copied",
      linkCopyFail: "Could not copy",
      actionCenter: "Action Center",
      actionCenterHint: "What needs you right now",
      allClear:
        "All clear — your twin is live, public and answering. New leads and matches land here.",
      noActivity:
        "No activity yet. Once visitors chat with your Twin, live events will appear here.",
      improveScore: "Improve score",
      act: {
        hot_one: "hot lead waiting for your follow-up",
        hot_other: "hot leads waiting for your follow-up",
        match_one: "new Exchange match to review",
        match_other: "new Exchange matches to review",
        gap_one: "question your twin could not answer — close the gaps",
        gap_other: "questions your twin could not answer — close the gaps",
        teach: "Teach your twin — index your website or upload documents",
        private: "Your twin is private — publish it to appear in the marketplace",
        firstDeal: "Post your first deal on the Exchange — matching runs automatically",
        usage: "{{used}} of {{limit}} conversations used — upgrade before your twin pauses",
      },
      loading: "Loading workspace…",
      welcomeBack: "Welcome back, {{name}}",
      twinHandled: "Your twin handled 218 conversations this week.",
      openTwin: "Open Twin",
      stats: {
        conversations: "Conversations",
        leads: "Leads generated",
        opportunities: "Opportunities",
        aiPerf: "AI Performance",
      },
      convLabel: "Conversations",
      last14: "Last 14 days",
      vsPrev: "+18% vs prev",
      leadPipeline: "Lead pipeline",
      thisWeek: "This week",
      liveActivity: "Live activity",
      realTime: "Real-time",
      twinStatus: "Twin status",
      live: "Live",
      pagesIndexed: "Pages indexed",
      documents: "Documents",
      knowledgeSize: "Knowledge size",
      chunks: "{{n}} chunks",
      aiScore: "AI score",
      openAITwin: "Open AI Twin",
    },
    locked: {
      welcome: "Welcome, {{name}}",
      subtitle: "Workspace overview.",
      hint: "Create your workspace to unlock Twin, Knowledge, Leads and the full dashboard.",
      none: {
        eyebrow: "Get started",
        title: "Get started",
        body: "Reality Twin is available now. Create your workspace to continue.",
        cta: "Create workspace",
      },
      pending: {
        eyebrow: "Workspace setup",
        title: "Your workspace is being prepared",
        body: "We're preparing your AI Twin workspace. Refresh shortly or contact us if this takes longer than expected.",
        cta: "Open workspace",
      },
      rejected: {
        eyebrow: "Workspace unavailable",
        title: "We couldn't load your workspace",
        body: "Write to hello@realitytwin.io and we'll help you get running.",
        cta: "Contact support",
      },
      error: {
        eyebrow: "Status couldn't be loaded",
        title: "Please reload the status",
        body: "Your workspace status couldn't be reliably checked right now.",
        cta: "Reload status",
      },
    },
    settings: {
      title: "Settings",
      subtitle: "Manage your account.",
      account: "Account",
      email: "Email",
      changePassword: "Change password",
      current: "Current password",
      new: "New password",
      confirm: "Confirm new password",
      update: "Update password",
      saving: "Saving…",
      session: "Session",
      signOut: "Sign out",
      tooShort: "Password must be at least 8 characters",
      mismatch: "Passwords don't match",
      wrongCurrent: "Current password is wrong",
      updated: "Password updated",
    },
  },
};

export default en;
export type Translation = typeof en;
